import React, { useState } from 'react';
import Image from 'next/image';
import { Translate, useTranslate } from '~/i18n';
import { CloseIcon } from '~/svgs';
import Modal from '~/components/common/modal';
import { Section } from '../common/section';
import { Button } from '../common/button';
import { bnplChangeState } from '~/apis/bnpl/bnblChangeState';
import { errorMessage } from '~/utils/toasts';
import { sentryCaptureException } from '~/errors/sentry';
import cn from 'classnames';
import { SubmitLoader } from '~/components/common/loaders/submit';
import styles from './styles.module.css';
import { BackArrowIcon } from '~/svgs/marketplace';
import { Switch } from '../common/forms/switch';

type BnplSetupModalType = {
   open: boolean;
   onClose: (showVerifyOtp?: boolean) => any;
   isBnplActive?: boolean;
   tabbyServiceRate: string;
   tamaraServiceRate: string;
};

type BnplOptionsProps = {
   tabby: boolean;
   setTabby: (value: any) => any;
   tamara: boolean;
   setTamara: (value: any) => any;
   setOptionsChanged?: (value: any) => any;
};

const BnplSetupModal = ({
   open,
   onClose,
   isBnplActive = false,
   tabbyServiceRate,
   tamaraServiceRate,
   tabby,
   setTabby,
   tamara,
   setTamara,
}: BnplSetupModalType & BnplOptionsProps) => {
   const [showTerms, setShowTerms] = useState(false);
   const [loading, setLoading] = useState(false);
   const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
   const [optionsChanged, setOptionsChanged] = useState(false);

   const changeState = async () => {
      try {
         setLoading(true);
         const { response, error } = await bnplChangeState({
            bnpl_agreed: Boolean(tabby || tamara) === true ? true : false,
            tabby_active: tabby,
            tamara_active: tamara,
         });
         if (error) {
            setLoading(false);
            sentryCaptureException(error, { tags: { journey: 'business' } });
            errorMessage(error?.response?.data?.message, 4000);
         }
         if (response) {
            setLoading(false);
            setShowTerms(false);
            onClose(true);
         }
      } catch (error: any) {
         sentryCaptureException(error, { tags: { journey: 'profile' } });
         console.info(error);
         setLoading(false);
      }
   };

   return (
      <Modal size={showTerms ? 'lg' : 'md'} open={open}>
         <Modal.Body>
            <Section className="py-2 rounded-2xl">
               <div className="flex justify-between w-full gap-4 px-8 py-2">
                  {showTerms ? (
                     <Button
                        onClick={() => setShowTerms(false)}
                        theme="outline-secondary"
                        className="!font-normal !px-5 flex items-center !border !border-grey-200">
                        <span className="ltr:mr-3 rtl:ml-3 ">
                           <BackArrowIcon width={18} height={18} className="rtl:rotate-180" />
                        </span>{' '}
                        <span className="hidden lg:block">
                           <Translate id="common:back" />
                        </span>
                     </Button>
                  ) : (
                     <div className="font-semibold">
                        <Translate id="bnpl:buy now pay later" />
                     </div>
                  )}

                  <div
                     onClick={() => {
                        setShowTerms(false);
                        onClose();
                     }}
                     className="cursor-pointer">
                     <CloseIcon />
                  </div>
               </div>
               <hr />

               {showTerms ? (
                  <div className="lg:p-8 p-6 max-h-56 lg:max-h-[700px] overflow-y-auto">
                     <article className={styles.article}>
                        <Translate
                           id="bnpl:bnpl terms"
                           parseHTML
                           values={{ tabbyRate: tabbyServiceRate, tamaraRate: tamaraServiceRate }}
                        />
                     </article>
                  </div>
               ) : !isBnplActive ? (
                  <>
                     <div className="flex flex-col px-8 py-6">
                        <span className="font-semibold">
                           <Translate id="bnpl:setup bnpl title" />
                        </span>
                        <span className="text-xs">
                           <Translate id="bnpl:setup bnpl subtitle" />
                        </span>
                        <BnplOptions
                           tabby={tabby}
                           setTabby={setTabby}
                           tamara={tamara}
                           setTamara={setTamara}
                           setOptionsChanged={setOptionsChanged}
                        />
                     </div>
                     <div className="flex flex-col mx-8 border rounded-lg bg-grey-600 border-grey-400">
                        <div className="w-full px-5 py-2 border-b border-grey-400">
                           <div className="font-semibold">
                              <Translate id="bnpl:service rates" />
                           </div>
                        </div>
                        <div className="flex flex-col gap-3 px-5 py-4">
                           <span className="text-sm">
                              <Translate
                                 parseHTML
                                 id="bnpl:tabby service rate"
                                 values={{ rate: tabbyServiceRate }}
                              />
                           </span>
                           <span className="text-sm">
                              <Translate
                                 parseHTML
                                 id="bnpl:tamara service rate"
                                 values={{ rate: tamaraServiceRate }}
                              />
                           </span>
                        </div>
                     </div>
                     <div className="flex gap-1 px-8 py-4 border-b border-grey-500">
                        <div>
                           <input
                              onClick={() => setTermsAccepted(!termsAccepted)}
                              type="checkbox"
                              defaultChecked={termsAccepted}
                              className="border-gray-200 rounded-sm text-primary focus:ring-white"
                           />
                        </div>
                        <span className="mx-1" onClick={() => setShowTerms(true)}>
                           <Translate parseHTML id="bnpl:agreed to terms" />
                        </span>
                     </div>
                  </>
               ) : (
                  <>
                     <div className="flex flex-col px-8 py-4">
                        <div className="flex items-center gap-3">
                           <span className="font-semibold">
                              <Translate id="bnpl:bnpl is active" />
                           </span>
                           <span className="rounded-full bg-green text-xs text-white px-2.5 py-1.5">
                              <Translate id="bnpl:active" />
                           </span>
                        </div>
                        <span className="text-xs">
                           <Translate id="bnpl:switch bnpl on/off" />
                        </span>
                        <BnplOptions
                           tabby={tabby}
                           setTabby={setTabby}
                           tamara={tamara}
                           setTamara={setTamara}
                           setOptionsChanged={setOptionsChanged}
                        />
                     </div>
                  </>
               )}
               <div className="flex items-center justify-between px-8 py-4 border-t">
                  <Button
                     onClick={() => {
                        setShowTerms(false);
                        onClose();
                     }}
                     theme="outline-primary"
                     className="h-10 !w-32">
                     <Translate id="common:cancel" />
                  </Button>
                  <Button
                     disabled={
                        loading ||
                        (!isBnplActive && !termsAccepted) ||
                        (!isBnplActive && !optionsChanged) ||
                        (!isBnplActive && termsAccepted && !tabby && !tamara)
                     }
                     onClick={() => changeState()}
                     theme={isBnplActive ? 'outline-primary' : 'primary'}
                     className={cn(
                        'h-10 !w-fit',
                        isBnplActive &&
                           '!w-48 !border-red !text-red hover:!bg-red hover:!text-white'
                     )}>
                     {loading ? (
                        <SubmitLoader size={20} width={'20'} />
                     ) : (
                        <Translate
                           id={
                              isBnplActive && tamara && tabby
                                 ? 'bnpl:activate'
                                 : isBnplActive && !(tabby || tamara)
                                 ? 'bnpl:deactivate bnpl'
                                 : 'common:continue'
                           }
                        />
                     )}
                  </Button>
               </div>
            </Section>
         </Modal.Body>
      </Modal>
   );
};

const BnplOptions = ({
   tabby,
   setTabby,
   tamara,
   setTamara,
   setOptionsChanged,
}: BnplOptionsProps) => {
   const { lang } = useTranslate();

   const handleSwitch = (service: string) => {
      // Update the state of the switches
      if (service === 'tabby') {
         setTabby((prevTabby: any) => !prevTabby);
      } else if (service === 'tamara') {
         setTamara((prevTamara: any) => !prevTamara);
      }

      // Check if there's an actual change in state
      const updatedOptionsChanged = (prevState: any) => !prevState;
      if (setOptionsChanged) setOptionsChanged(updatedOptionsChanged);
   };

   return (
      <div className="flex flex-col w-full gap-3 mt-4 border rounded-lg border-grey-400">
         <div className="flex items-center justify-between w-full gap-8 p-4 border-b border-grey-400 ">
            <div className="flex flex-col">
               <div className="flex items-center gap-2 font-semibold">
                  <Translate id="bnpl:tabby" />
                  <Image priority width={56} height={25} src={`/images/brands/tabby.svg`} />
               </div>
               <span className="text-sm text-grey-200">
                  {!tabby ? <Translate id="bnpl:inactive" /> : <Translate id="bnpl:active" />}
               </span>
            </div>
            <Switch id="tabby" open={tabby} onChange={() => handleSwitch('tabby')} />
         </div>

         <div className="flex items-center justify-between w-full gap-8 p-4 ">
            <div className="flex flex-col">
               <div className="flex items-center gap-2 font-semibold">
                  <Translate id="bnpl:tamara" />
                  <Image
                     priority
                     width={56}
                     height={25}
                     src={`/images/brands/tamara-${lang || 'en'}.svg`}
                  />
               </div>
               <span className="text-sm text-grey-200">
                  {!tamara ? <Translate id="bnpl:inactive" /> : <Translate id="bnpl:active" />}
               </span>
            </div>
            <Switch id="tamara" open={tamara} onChange={() => handleSwitch('tamara')} />
         </div>
      </div>
   );
};

export default BnplSetupModal;
